import type { TeamState } from '@nimbalyst/collab-protocol';
import type { BrowserSessionJwt, TeamJwt } from './auth/teamJwt';
import { consoleConfig } from './config';

type TeamIdentity = Pick<NonNullable<TeamState['metadata']>, 'orgId' | 'name'>;

export type TeamSummary = TeamIdentity & {
  /** The route helper accepts a future canonical slug; the current API omits it. */
  orgSlug?: string | null;
  role: string;
  membershipType: string;
  createdAt: string;
  teamMemberId?: string | null;
  projects: Array<{
    projectId: string;
    teamProjectId: string;
    slug?: string | null;
    name: string | null;
    /**
     * The project's git remote. Present only for `active_member` membership --
     * `handleListTeams` strips it for pending and invited members -- so treat
     * absence as "not disclosed", never as "no remote".
     */
    remoteUrl?: string | null;
  }>;
};

export interface TeamMember {
  memberId: string;
  email: string;
  name: string;
  status: string;
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'guest';
  createdAt: string;
}

export interface MembersResponse {
  members: TeamMember[];
  callerRole: string;
  callerMemberId: string;
}

export interface BillingState {
  plan: string;
  seatCount: number;
  status: string;
  currentPeriodEnd: number | null;
}

export interface KeyStatus {
  mode: string;
  dekEpoch: number | null;
  dekFingerprint: string | null;
}

export interface AuditEntry {
  id: string;
  ts: number;
  actorUserId: string | null;
  action: string;
  target: string | null;
  detail: Record<string, unknown> | null;
}

interface TeamsResponse { teams: TeamSummary[] }
interface AuditResponse { entries: AuditEntry[] }

export class ApiError extends Error {
  readonly status: number;

  constructor(
    status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function apiRequest<T>(
  sessionJwt: BrowserSessionJwt | TeamJwt,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('Authorization', `Bearer ${sessionJwt}`);
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${consoleConfig.collabApiBaseUrl}${path}`, {
    ...init,
    credentials: 'omit',
    headers,
  });
  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let detail = raw;
    try {
      const parsed = JSON.parse(raw) as { error?: string };
      detail = parsed.error || raw;
    } catch {
      // Plain-text errors are already useful.
    }
    throw new ApiError(response.status, detail || `Request failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function teamPath(orgId: string, suffix = ''): string {
  return `/api/teams/${encodeURIComponent(orgId)}${suffix}`;
}

export async function listTeams(sessionJwt: BrowserSessionJwt, signal?: AbortSignal): Promise<TeamSummary[]> {
  const data = await apiRequest<TeamsResponse>(sessionJwt, '/api/teams', { signal });
  return Array.isArray(data.teams) ? data.teams : [];
}

/**
 * Creates an organization with the caller as its admin and one project. Any
 * browser session works; the server finds the creator's email from it.
 */
export function createTeam(sessionJwt: BrowserSessionJwt, name: string): Promise<{ orgId: string; name: string }> {
  return apiRequest(sessionJwt, '/api/teams', { method: 'POST', body: JSON.stringify({ name }) });
}

/** Admin only; the server re-checks the role. */
export function addProject(
  sessionJwt: TeamJwt,
  orgId: string,
  name: string,
): Promise<{ projectId: string; teamProjectId: string }> {
  return apiRequest(sessionJwt, teamPath(orgId, '/projects'), { method: 'POST', body: JSON.stringify({ name }) });
}

export function listMembers(sessionJwt: TeamJwt, orgId: string, signal?: AbortSignal): Promise<MembersResponse> {
  return apiRequest(sessionJwt, teamPath(orgId, '/members'), { signal });
}

export function inviteMember(
  sessionJwt: TeamJwt,
  orgId: string,
  email: string,
  role?: TeamMember['role'],
  returnPath?: string,
): Promise<{ success: true; email: string }> {
  return apiRequest(sessionJwt, teamPath(orgId, '/invite'), {
    method: 'POST',
    body: JSON.stringify({
      email,
      ...(role ? { role } : {}),
      ...(returnPath ? { returnPath } : {}),
    }),
  });
}

export function updateMemberRole(
  sessionJwt: TeamJwt,
  orgId: string,
  memberId: string,
  role: TeamMember['role'],
): Promise<{ success: true }> {
  return apiRequest(sessionJwt, teamPath(orgId, `/members/${encodeURIComponent(memberId)}`), {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export function removeMember(sessionJwt: TeamJwt, orgId: string, memberId: string): Promise<{ success: true }> {
  return apiRequest(sessionJwt, teamPath(orgId, `/members/${encodeURIComponent(memberId)}`), {
    method: 'DELETE',
  });
}

export interface InviteLink {
  id: string;
  role: TeamMember['role'];
  createdByEmail: string;
  createdAt: string;
  expiresAt: string;
  maxUses: number | null;
  useCount: number;
  status: 'active' | 'revoked' | 'expired' | 'used_up';
}

/** Admin only. The raw link is in this response and nowhere else; the server keeps only its hash. */
export function createInviteLink(
  sessionJwt: TeamJwt,
  orgId: string,
  options: { role: TeamMember['role']; expiresInSeconds: number; maxUses?: number },
): Promise<{ url: string; link: InviteLink }> {
  return apiRequest(sessionJwt, teamPath(orgId, '/invite-links'), { method: 'POST', body: JSON.stringify(options) });
}

export async function listInviteLinks(sessionJwt: TeamJwt, orgId: string, signal?: AbortSignal): Promise<InviteLink[]> {
  const data = await apiRequest<{ links?: InviteLink[] }>(sessionJwt, teamPath(orgId, '/invite-links'), { signal });
  return Array.isArray(data.links) ? data.links : [];
}

export function revokeInviteLink(sessionJwt: TeamJwt, orgId: string, linkId: string): Promise<{ success: true }> {
  return apiRequest(sessionJwt, teamPath(orgId, `/invite-links/${encodeURIComponent(linkId)}`), { method: 'DELETE' });
}

export interface InviteLinkPreview {
  orgId: string;
  teamName: string | null;
  role: TeamMember['role'];
  expiresAt: string;
  alreadyMember: boolean;
}

/** Any signed-in session; the server joins the session's verified email, never one the page names. */
export function describeInviteLink(sessionJwt: BrowserSessionJwt, token: string): Promise<InviteLinkPreview> {
  return apiRequest(sessionJwt, '/api/invite-links/describe', { method: 'POST', body: JSON.stringify({ token }) });
}

export function redeemInviteLink(sessionJwt: BrowserSessionJwt, token: string): Promise<{ orgId: string; alreadyMember: boolean }> {
  return apiRequest(sessionJwt, '/api/invite-links/redeem', { method: 'POST', body: JSON.stringify({ token }) });
}

/**
 * An outstanding invitation is a Stytch member who never completed the magic
 * link, so revoking it is the same delete that removes a joined member. Named
 * separately because the console says "revoke" and "remove" to the admin and
 * the two must stay legible at the call site.
 */
export const revokeInvitation = removeMember;

/**
 * Re-issuing the invite mails a fresh magic link to the same address. Stytch
 * treats a repeat invite for an existing pending member as a resend, so no
 * separate endpoint is needed -- and the role is re-validated server-side on
 * the way through, exactly as it was the first time.
 */
export const resendInvitation = inviteMember;

export interface ProjectAccessGrant {
  userId: string;
  projectRole: string;
}

export async function listProjectAccess(
  sessionJwt: TeamJwt,
  orgId: string,
  projectId: string,
  signal?: AbortSignal,
): Promise<ProjectAccessGrant[]> {
  const data = await apiRequest<{ grants?: ProjectAccessGrant[] }>(
    sessionJwt,
    teamPath(orgId, `/project-access?projectId=${encodeURIComponent(projectId)}`),
    { signal },
  );
  return Array.isArray(data.grants) ? data.grants : [];
}

export function grantProjectAccess(
  sessionJwt: TeamJwt,
  orgId: string,
  projectId: string,
  userId: string,
  projectRole: string,
): Promise<{ success: true }> {
  return apiRequest(sessionJwt, teamPath(orgId, '/project-access'), {
    method: 'POST',
    body: JSON.stringify({ projectId, userId, projectRole }),
  });
}

export function revokeProjectAccess(
  sessionJwt: TeamJwt,
  orgId: string,
  projectId: string,
  userId: string,
): Promise<{ success: true }> {
  const query = `projectId=${encodeURIComponent(projectId)}&userId=${encodeURIComponent(userId)}`;
  return apiRequest(sessionJwt, teamPath(orgId, `/project-access?${query}`), { method: 'DELETE' });
}

export function getBilling(sessionJwt: TeamJwt, orgId: string, signal?: AbortSignal): Promise<BillingState> {
  return apiRequest(sessionJwt, teamPath(orgId, '/billing'), { signal });
}

export function createCheckoutSession(
  sessionJwt: TeamJwt,
  orgId: string,
  seats: number,
): Promise<{ url: string }> {
  return apiRequest(sessionJwt, teamPath(orgId, '/billing/checkout-session'), {
    method: 'POST',
    body: JSON.stringify({ seats }),
  });
}

export function createPortalSession(sessionJwt: TeamJwt, orgId: string): Promise<{ url: string }> {
  return apiRequest(sessionJwt, teamPath(orgId, '/billing/portal'), {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function getKeyStatus(sessionJwt: TeamJwt, orgId: string, signal?: AbortSignal): Promise<KeyStatus> {
  return apiRequest(sessionJwt, teamPath(orgId, '/key-status'), { signal });
}

export async function getAuditLog(
  sessionJwt: TeamJwt,
  orgId: string,
  signal?: AbortSignal,
): Promise<AuditEntry[]> {
  const data = await apiRequest<AuditResponse>(sessionJwt, teamPath(orgId, '/audit-log?limit=20'), { signal });
  return Array.isArray(data.entries) ? data.entries : [];
}

export function deleteOrganization(sessionJwt: TeamJwt, orgId: string): Promise<{ success: true }> {
  return apiRequest(sessionJwt, teamPath(orgId), { method: 'DELETE' });
}

export function mergeOrganization(
  sessionJwt: TeamJwt,
  drainedOrgId: string,
  survivorOrgId: string,
  deleteDrained: boolean,
): Promise<{ drainedDeleted: boolean; partial: boolean; error?: string }> {
  return apiRequest(sessionJwt, teamPath(drainedOrgId, '/merge-into'), {
    method: 'POST',
    body: JSON.stringify({ survivorOrgId, deleteDrained }),
  });
}
